import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import {
  APAGAR_HONORARIOS,
  atualizarCliente,
  carregarMapaCalculo,
  carregarParametros,
  carregarPropostas,
  carregarQuestionario,
  criarProposta,
  formatarCpf,
  formatarReais,
  formatarTelefone,
  marcarVista,
  meuLink,
  moverProposta,
  nomeDoCliente,
  rotuloDoStatus,
  STATUS_IRP,
  urlDoFormulario,
  type MapaCalculoLido,
  type ParametrosLidos,
  type Pergunta,
  type Proposta,
  type StatusIrp,
} from "../lib/irproposta";
import { formatarData } from "../lib/dados";
import { buscarCep } from "../lib/cep";
import {
  cepCompleto,
  cpfValido,
  emailValido,
  mascararCep,
  mascararCpf,
  mascararMoeda,
  mascararTelefone,
  mascararUf,
  moedaDeNumero,
} from "../lib/mascaras";
import Carregando from "../componentes/Carregando";
import CalculadoraHonorarios from "../componentes/CalculadoraHonorarios";
import { CAMPO, Recado, Secao, SetaDireita } from "../componentes/ui";
import { Cabine } from "../componentes/FerramentaUi";
import { TileDeFato } from "../componentes/CalendarioUi";

// IR Proposta — a primeira ferramenta que MORA no hub.
//
// As nove portas abertas antes desta (000062 e 000064) eram destino para software
// que já existia em outro lugar. Aqui a ferramenta é a tela.
//
// ---------------------------------------------------------------------------
// O que mudou em relação ao Bubble, e por quê
// ---------------------------------------------------------------------------
// O Bubble tem quatro itens de menu lateral: Dashboard, Propostas, Calculadora e
// Gerar Contrato. Aqui são três abas, e o contrato saiu de aba para virar AÇÃO DA
// PROPOSTA. A razão é o dado: o contrato precisa do CPF e do endereço de um
// cliente específico, e uma tela separada obrigaria o aluno a escolher o cliente
// de novo — depois de já ter aberto a proposta dele. Na prática, "Gerar Contrato"
// como menu é um seletor de cliente com um botão embaixo.
//
// A Calculadora é a terceira aba. Ela não veio do Bubble: existia como app
// separado feito no Lovable (repositório `calculadora-irpf`), com login por
// e-mail em localStorage e os treze valores de referência morrendo a cada
// recarga da página. A regra dele está preservada em `lib/calculadoraIrpf.ts`, e
// o que mudou está no cabeçalho de `componentes/CalculadoraHonorarios.tsx` e da
// migration 000071.
//
// O que só era possível aqui: a simulação nasce com as RESPOSTAS DO CLIENTE, e o
// resultado grava na proposta — de onde entra no contrato, por extenso.
//
// ---------------------------------------------------------------------------
// Nada aqui decide acesso
// ---------------------------------------------------------------------------
// Quem decide se o aluno chega nesta tela é `vw_minha_vitrine` (o card não aparece
// para quem não tem a Escola, e curso trancado devolve `em-breve`). Quem decide o
// que ele vê aqui dentro é a RLS de `irp_propostas`. Esta tela agrupa e desenha.

type Aba = "respostas" | "propostas" | "calculadora";

export default function IrProposta() {
  const [propostas, setPropostas] = useState<Proposta[]>([]);
  const [questionario, setQuestionario] = useState<Pergunta[]>([]);
  const [token, setToken] = useState<string | null>(null);
  const [estado, setEstado] = useState<"carregando" | "pronto" | "erro">("carregando");
  const [aba, setAba] = useState<Aba>("respostas");
  const [aberta, setAberta] = useState<string | null>(null);
  const [parametros, setParametros] = useState<ParametrosLidos | null>(null);
  const [mapa, setMapa] = useState<MapaCalculoLido[]>([]);
  /**
   * De quem é o cálculo da aba Calculadora.
   *
   * Mora AQUI, e não dentro da calculadora, porque o detalhe da proposta precisa
   * poder mandar o aluno para lá já com o cliente escolhido — "calcular os
   * honorários DESTE cliente" é o caminho que a ferramenta existe para servir, e
   * obrigá-lo a reescolher no `select` depois de já ter aberto a proposta seria o
   * mesmo defeito que tirou "Gerar Contrato" do menu.
   */
  const [alvoDoCalculo, setAlvoDoCalculo] = useState<string | null>(null);

  const recarregar = useCallback(async () => {
    setPropostas(await carregarPropostas());
  }, []);

  const recarregarParametros = useCallback(async () => {
    setParametros(await carregarParametros());
  }, []);

  useEffect(() => {
    let ativo = true;
    (async () => {
      // O link é criado na primeira visita à ferramenta, não no cadastro do aluno:
      // 59.697 perfis não precisam de token de formulário para existir.
      const [lista, perguntas, link, valores, mapaCalc] = await Promise.all([
        carregarPropostas(),
        carregarQuestionario(),
        meuLink(),
        carregarParametros(),
        carregarMapaCalculo(),
      ]);
      if (!ativo) return;
      setPropostas(lista);
      setQuestionario(perguntas);
      setToken(link.ok ? link.res.token : null);
      setParametros(valores);
      setMapa(mapaCalc);
      setEstado(link.ok ? "pronto" : "erro");
    })();
    return () => {
      ativo = false;
    };
  }, []);

  if (estado === "carregando") return <Carregando />;

  const propostaAberta = propostas.find((p) => p.id === aberta) ?? null;

  // Os fatos da cabine: o funil em três números, todos medidos nas propostas que
  // já estão em memória — nenhuma chamada a mais. "Fechadas" são os quatro status
  // depois da negociação, que é o que o aluno quer saber ao abrir a ferramenta;
  // "a fazer" é o trabalho em cima da mesa. Sem proposta nenhuma, nenhum fato:
  // três zeros grandes na entrada é ruído, e a tela já explica o que fazer.
  const naoVistas = propostas.filter((p) => !p.visualizada).length;
  const fechadas = propostas.filter((p) =>
    ["documentacao", "dirpf_em_elaboracao", "entregue", "processada"].includes(p.status),
  ).length;
  const emNegociacao = propostas.filter((p) => p.status === "em_negociacao").length;

  return (
    <main className="mx-auto max-w-6xl px-4 py-7 sm:px-6">
      <Cabine
        rotulo="Ferramenta"
        titulo="IR Proposta"
        descricao="Mande o formulário ao seu cliente, receba as respostas e acompanhe a proposta até a entrega da declaração."
        fatos={
          propostas.length > 0 ? (
            <>
              <TileDeFato
                rotulo="Respostas"
                valor={propostas.length}
                apoio={naoVistas > 0 ? `${naoVistas} ainda não vista${naoVistas > 1 ? "s" : ""}` : "todas vistas"}
              />
              <TileDeFato rotulo="Em negociação" valor={emNegociacao} apoio="esperando você" />
              <TileDeFato rotulo="Em andamento" valor={fechadas} apoio="da documentação à entrega" />
            </>
          ) : undefined
        }
      />

      {estado === "erro" && (
        <Recado
          tipo="erro"
          texto="Não conseguimos preparar o seu link de formulário agora. Recarregue a página; se continuar, fale com o suporte."
        />
      )}

      {/* As abas em pílula sobre `surface`, como as do HUB DP — a mesma peça
          visual em toda ferramenta de várias seções. Continuam `useState` e não
          sub-rota, ao contrário do HUB DP, porque aqui a aba compartilha estado
          com o detalhe da proposta: "calcular os honorários DESTE cliente" troca
          de aba levando o alvo do cálculo, e um link para "?aba=calculadora" sem
          o alvo abriria o `select` vazio. Quando a ferramenta ganhar rota por
          proposta, as abas viram rota junto. */}
      <nav
        className="mt-6 mb-7 overflow-x-auto px-1 pb-1"
        aria-label="Seções da ferramenta"
      >
        <ul className="surface flex min-w-max gap-1 rounded-full p-1.5">
          {(
            [
              ["respostas", "Respostas"],
              ["propostas", "Propostas"],
              ["calculadora", "Calculadora"],
            ] as Array<[Aba, string]>
          ).map(([chave, rotulo]) => (
            <li key={chave}>
              <button
                type="button"
                onClick={() => setAba(chave)}
                aria-current={aba === chave ? "page" : undefined}
                className={`block rounded-full px-4 py-2 text-sm font-semibold whitespace-nowrap transition ${
                  aba === chave
                    ? "bg-azul text-navy-ink shadow-[0_6px_16px_-8px_rgb(16_40_80/0.6)]"
                    : "text-cinza hover:bg-chip hover:text-tinta"
                }`}
              >
                {rotulo}
              </button>
            </li>
          ))}
        </ul>
      </nav>

      <div className="mt-7 space-y-6">
        {aba === "respostas" && (
          <Respostas
            propostas={propostas}
            token={token}
            aoAbrir={setAberta}
            aoRecarregar={recarregar}
          />
        )}
        {aba === "propostas" && (
          <Kanban propostas={propostas} aoAbrir={setAberta} aoRecarregar={recarregar} />
        )}
        {aba === "calculadora" && parametros && (
          <CalculadoraHonorarios
            propostas={propostas}
            questionario={questionario}
            mapa={mapa}
            parametros={parametros}
            alvo={alvoDoCalculo}
            aoTrocarAlvo={setAlvoDoCalculo}
            aoRecarregar={recarregar}
            aoRecarregarParametros={recarregarParametros}
          />
        )}
      </div>

      {propostaAberta && (
        <Detalhe
          proposta={propostaAberta}
          questionario={questionario}
          aoFechar={() => setAberta(null)}
          aoRecarregar={recarregar}
          aoCalcular={() => {
            setAberta(null);
            setAlvoDoCalculo(propostaAberta.id);
            setAba("calculadora");
          }}
        />
      )}
    </main>
  );
}

// ---------------------------------------------------------------------------
// Aba 1 — respostas: os três números, o link e a lista
// ---------------------------------------------------------------------------
function Respostas({
  propostas,
  token,
  aoAbrir,
  aoRecarregar,
}: {
  propostas: Proposta[];
  token: string | null;
  aoAbrir: (id: string) => void;
  aoRecarregar: () => Promise<void>;
}) {
  const [filtro, setFiltro] = useState<"todas" | "vistas" | "novas">("todas");
  const [busca, setBusca] = useState("");

  const lista = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return propostas
      .filter((p) =>
        filtro === "todas" ? true : filtro === "vistas" ? p.visualizada : !p.visualizada,
      )
      .filter((p) => {
        if (!termo) return true;
        // Busca por nome também, e não só por e-mail como no Bubble: metade das
        // respostas reais chega sem e-mail, e um filtro que não acha quem não tem
        // e-mail é um filtro que esconde lead.
        return (
          (p.email ?? "").toLowerCase().includes(termo) ||
          p.nome.toLowerCase().includes(termo) ||
          (p.telefone ?? "").includes(termo)
        );
      });
  }, [propostas, filtro, busca]);

  return (
    <>
      {/* Os três números saíram daqui e foram para os cartões de vidro da cabine
          (26/08). Estavam ditos DUAS vezes na mesma tela depois do redesenho —
          "Respostas recebidas 3" em card grande logo abaixo de "RESPOSTAS 3" no
          hero. É a cicatriz do IR Pro: quando a tela parece cheia e não diz mais
          nada, conte quantas vezes a mesma informação aparece. O que ficou aqui
          é o que a cabine não dá: o link, os filtros e a lista. */}
      <LinkDoFormulario token={token} />

      <Secao
        titulo="Todas as respostas"
        descricao={
          propostas.length === 0
            ? undefined
            : `${lista.length} de ${propostas.length} ${propostas.length === 1 ? "resposta" : "respostas"}`
        }
        i={5}
      >
        <div className="flex flex-wrap items-center gap-2">
          {(
            [
              ["todas", "Todas"],
              ["vistas", "Visualizadas"],
              ["novas", "Não vistas"],
            ] as Array<["todas" | "vistas" | "novas", string]>
          ).map(([chave, rotulo]) => (
            <button
              key={chave}
              type="button"
              onClick={() => setFiltro(chave)}
              aria-pressed={filtro === chave}
              className={`inline-flex min-h-11 items-center rounded-full px-4 text-sm font-semibold transition ${
                filtro === chave ? "bg-azul text-white" : "bg-chip text-chip-tinta hover:bg-tile"
              }`}
            >
              {rotulo}
            </button>
          ))}
          <label className="ml-auto min-w-56 flex-1">
            <span className="sr-only">Filtrar por nome, e-mail ou telefone</span>
            <input
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Filtrar por nome, e-mail ou telefone"
              className={CAMPO}
            />
          </label>
        </div>

        {propostas.length === 0 ? (
          <Vazio>
            Nenhuma resposta ainda. Copie o link acima e mande ao seu cliente — as respostas dele
            aparecem aqui.
          </Vazio>
        ) : lista.length === 0 ? (
          <Vazio>Nenhuma resposta com esse filtro.</Vazio>
        ) : (
          <ListaDeRespostas lista={lista} aoAbrir={aoAbrir} aoRecarregar={aoRecarregar} />
        )}
      </Secao>
    </>
  );
}

/**
 * O link do formulário.
 *
 * Rotacionar existe porque o link vai por WhatsApp e sobrevive à conversa: sem
 * troca, um link vazado não tem como ser cortado. E a confirmação existe porque o
 * corte é IRREVERSÍVEL para quem já recebeu o antigo — inclusive clientes no meio
 * de um preenchimento.
 */
function LinkDoFormulario({ token }: { token: string | null }) {
  const [atual, setAtual] = useState(token);
  const [copiado, setCopiado] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [recado, setRecado] = useState<{ tipo: "ok" | "erro"; texto: string } | null>(null);

  useEffect(() => setAtual(token), [token]);

  if (!atual) return null;
  const url = urlDoFormulario(atual);

  async function copiar() {
    try {
      await navigator.clipboard.writeText(url);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2500);
    } catch {
      // Clipboard negado (http, permissão, navegador antigo): o input é
      // selecionável, então o aluno copia à mão. Melhor que um erro que não diz o
      // que fazer.
      setRecado({ tipo: "erro", texto: "Seu navegador não deixou copiar. Selecione o link e copie." });
    }
  }

  async function rotacionar() {
    const r = await meuLink(true);
    setConfirmando(false);
    if (!r.ok) return setRecado({ tipo: "erro", texto: r.mensagem });
    setAtual(r.res.token);
    setRecado({
      tipo: "ok",
      texto: "Link novo criado. O link antigo parou de receber respostas — mande o novo aos clientes.",
    });
  }

  return (
    <Secao
      titulo="O link do seu formulário"
      descricao="Mande este link aos seus clientes. É só ele que faz a resposta cair aqui na sua conta."
      i={4}
    >
      <div className="flex flex-wrap items-center gap-3">
        <input
          readOnly
          value={url}
          onFocus={(e) => e.currentTarget.select()}
          className={`${CAMPO} min-w-64 flex-1 font-mono text-sm`}
          aria-label="Link do seu formulário"
        />
        <button type="button" onClick={copiar} className="pill-cta shrink-0">
          {copiado ? "Copiado!" : "Copiar link"}
          {!copiado && <SetaDireita />}
        </button>
      </div>

      <div className="mt-4">
        {confirmando ? (
          <div className="rounded-xl bg-alerta-fundo p-4">
            <p className="text-sm leading-relaxed text-alerta-tinta">
              <strong>Trocar o link corta o antigo.</strong> Quem já recebeu o link anterior — e
              quem estiver preenchendo agora — vai ver "este link não está ativo". Faça isso se o
              link vazou ou foi para a pessoa errada.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button type="button" onClick={rotacionar} className="pill-cta">
                Trocar mesmo assim
              </button>
              <button type="button" onClick={() => setConfirmando(false)} className="btn-contorno">
                Cancelar
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setConfirmando(true)}
            className="text-sm font-semibold text-link underline decoration-link/30 underline-offset-4 hover:decoration-link"
          >
            Gerar um link novo
          </button>
        )}
      </div>

      {recado && <Recado tipo={recado.tipo} texto={recado.texto} />}
    </Secao>
  );
}

function ListaDeRespostas({
  lista,
  aoAbrir,
  aoRecarregar,
}: {
  lista: Proposta[];
  aoAbrir: (id: string) => void;
  aoRecarregar: () => Promise<void>;
}) {
  async function abrir(p: Proposta) {
    aoAbrir(p.id);
    // Abrir É visualizar. Marcar aqui, e não num botão separado, é o que faz o
    // contador de "não vistas" significar algo — no Bubble, 3.456 de 4.309 estão
    // marcadas como vistas justamente porque a marcação segue o ato de olhar.
    if (!p.visualizada) {
      await marcarVista(p.id);
      await aoRecarregar();
    }
  }

  return (
    // O `overflow-x-auto` é do container, nunca do body: tabela larga no celular
    // tem de rolar dentro dela mesma.
    <div className="mt-5 -mx-2 overflow-x-auto px-2">
      <table className="w-full min-w-160 border-collapse text-sm">
        <thead>
          <tr className="border-b border-borda text-left">
            {["Cliente", "Contato", "Recebida em", "Situação", ""].map((c) => (
              <th key={c} scope="col" className="pb-3 pr-4 font-semibold text-titulo">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {lista.map((p) => (
            <tr key={p.id} className="border-b border-borda/60 last:border-0">
              <td className="py-3 pr-4">
                <span className="flex items-center gap-2">
                  {!p.visualizada && (
                    <span
                      className="size-2 shrink-0 rounded-full bg-laranja"
                      title="Ainda não vista"
                      aria-label="Ainda não vista"
                    />
                  )}
                  <span className="font-semibold text-tinta">{nomeDoCliente(p)}</span>
                </span>
                {p.origem === "manual" && (
                  <span className="mt-1 inline-flex rounded-full bg-chip px-2 py-0.5 text-xs font-semibold text-chip-tinta">
                    cadastro seu
                  </span>
                )}
              </td>
              <td className="py-3 pr-4 text-cinza">
                <span className="block">{p.email ?? "—"}</span>
                <span className="block tabular-nums">{formatarTelefone(p.telefone)}</span>
              </td>
              <td className="py-3 pr-4 tabular-nums text-cinza">{formatarData(p.criado_em)}</td>
              <td className="py-3 pr-4">
                <span className="inline-flex rounded-full bg-tile px-2.5 py-0.5 text-xs font-semibold text-titulo">
                  {rotuloDoStatus(p.status)}
                </span>
              </td>
              <td className="py-3 text-right">
                <button
                  type="button"
                  onClick={() => abrir(p)}
                  className="text-sm font-semibold text-link underline decoration-link/30 underline-offset-4 hover:decoration-link"
                >
                  Ver respostas
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Aba 2 — o kanban
// ---------------------------------------------------------------------------
function Kanban({
  propostas,
  aoAbrir,
  aoRecarregar,
}: {
  propostas: Proposta[];
  aoAbrir: (id: string) => void;
  aoRecarregar: () => Promise<void>;
}) {
  const [arrastando, setArrastando] = useState<string | null>(null);
  const [sobre, setSobre] = useState<StatusIrp | null>(null);
  const [recado, setRecado] = useState<{ tipo: "ok" | "erro"; texto: string } | null>(null);
  const [novaAberta, setNovaAberta] = useState(false);

  async function mover(id: string, status: StatusIrp) {
    setArrastando(null);
    setSobre(null);
    const r = await moverProposta(id, status);
    if (!r.ok) return setRecado({ tipo: "erro", texto: r.mensagem });
    setRecado(null);
    await aoRecarregar();
  }

  return (
    <Secao
      titulo="Propostas"
      descricao="Arraste os cards entre as colunas, ou use o seletor de cada card."
      acao={
        <button type="button" onClick={() => setNovaAberta(true)} className="pill-cta shrink-0">
          Nova proposta
        </button>
      }
      i={1}
    >
      {novaAberta && (
        <NovaProposta
          aoFechar={() => setNovaAberta(false)}
          aoCriar={async () => {
            setNovaAberta(false);
            await aoRecarregar();
          }}
        />
      )}

      {recado && <Recado tipo={recado.tipo} texto={recado.texto} />}

      {/* Rola na horizontal dentro do próprio container: oito colunas não cabem em
          tela nenhuma, e o body do hub nunca rola na horizontal. */}
      <div className="mt-5 -mx-2 flex gap-4 overflow-x-auto px-2 pb-3">
        {STATUS_IRP.map(({ chave, rotulo }) => {
          const daColuna = propostas.filter((p) => p.status === chave);
          return (
            <div
              key={chave}
              onDragOver={(e) => {
                // `preventDefault` no dragover é o que autoriza o drop. Sem ele o
                // navegador recusa o soltar e nada acontece — sem erro nenhum.
                e.preventDefault();
                setSobre(chave);
              }}
              onDragLeave={() => setSobre((s) => (s === chave ? null : s))}
              onDrop={() => arrastando && mover(arrastando, chave)}
              className={`flex w-64 shrink-0 flex-col rounded-2xl border-[1.5px] p-3 transition ${
                sobre === chave ? "border-laranja bg-laranja/5" : "border-borda bg-superficie-2"
              }`}
            >
              <h3 className="flex items-baseline justify-between gap-2 pb-2 text-sm font-bold text-titulo">
                <span>{rotulo}</span>
                <span className="tabular-nums text-cinza">{daColuna.length}</span>
              </h3>

              <div className="flex min-h-40 flex-col gap-2">
                {daColuna.map((p) => (
                  <article
                    key={p.id}
                    draggable
                    onDragStart={() => setArrastando(p.id)}
                    onDragEnd={() => setArrastando(null)}
                    className={`rounded-xl bg-superficie p-3 shadow-sm transition ${
                      arrastando === p.id ? "opacity-50" : ""
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => aoAbrir(p.id)}
                      className="text-left font-semibold text-tinta hover:text-link"
                    >
                      {nomeDoCliente(p)}
                    </button>
                    {p.email && <p className="mt-0.5 truncate text-xs text-cinza">{p.email}</p>}
                    {p.telefone && (
                      <p className="text-xs tabular-nums text-cinza">{formatarTelefone(p.telefone)}</p>
                    )}
                    {p.honorarios !== null && (
                      <p className="mt-1 text-xs font-semibold text-laranja-texto tabular-nums">
                        {formatarReais(p.honorarios)}
                      </p>
                    )}

                    {/* O seletor não é redundância do arrastar: arrastar não
                        funciona em toque nem com teclado, e a maioria do público do
                        hub está no celular. */}
                    <label className="mt-2 block">
                      <span className="sr-only">Mudar a situação de {nomeDoCliente(p)}</span>
                      <select
                        value={p.status}
                        onChange={(e) => mover(p.id, e.target.value as StatusIrp)}
                        className="h-9 w-full rounded-lg border border-borda bg-superficie px-2 text-xs text-tinta focus:border-laranja focus:outline-none"
                      >
                        {STATUS_IRP.map((s) => (
                          <option key={s.chave} value={s.chave}>
                            {s.rotulo}
                          </option>
                        ))}
                      </select>
                    </label>
                  </article>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </Secao>
  );
}

function NovaProposta({
  aoFechar,
  aoCriar,
}: {
  aoFechar: () => void;
  aoCriar: () => Promise<void>;
}) {
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [telefone, setTelefone] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setErro(null);
    if (!email.trim() && !telefone.trim()) {
      return setErro("Preencha ao menos o e-mail ou o telefone — é como você vai falar com ele.");
    }
    if (email.trim() !== "" && !emailValido(email)) {
      return setErro("O e-mail não parece válido. Confira o endereço.");
    }
    setSalvando(true);
    const r = await criarProposta({ nome, email, telefone });
    setSalvando(false);
    if (!r.ok) return setErro(r.mensagem);
    await aoCriar();
  }

  return (
    <form onSubmit={enviar} className="mt-4 rounded-xl bg-superficie-2 p-4">
      <p className="text-sm text-cinza">
        Para o cliente que já falou com você por fora. O questionário fica vazio — quem responde é
        o cliente, pelo link.
      </p>
      <div className="mt-3 grid gap-3 sm:grid-cols-3">
        <label className="block">
          <span className="mb-1 block text-sm font-semibold text-titulo">Nome</span>
          <input value={nome} onChange={(e) => setNome(e.target.value)} required minLength={2} className={CAMPO} />
        </label>
        <label className="block">
          <span className="mb-1 block text-sm font-semibold text-titulo">E-mail</span>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={CAMPO} />
        </label>
        <label className="block">
          <span className="mb-1 block text-sm font-semibold text-titulo">Telefone</span>
          <input
            value={telefone}
            onChange={(e) => setTelefone(mascararTelefone(e.target.value))}
            inputMode="tel"
            placeholder="(11) 90000-0000"
            className={CAMPO}
          />
        </label>
      </div>
      <p className="mt-2 text-sm text-cinza">Preencha ao menos o e-mail ou o telefone.</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="submit" disabled={salvando} className="pill-cta disabled:opacity-60">
          {salvando ? "Criando…" : "Criar proposta"}
        </button>
        <button type="button" onClick={aoFechar} className="btn-contorno">
          Cancelar
        </button>
      </div>
      {erro && <Recado tipo="erro" texto={erro} />}
    </form>
  );
}

// ---------------------------------------------------------------------------
// O detalhe da proposta — respostas, dados do cliente e o contrato
// ---------------------------------------------------------------------------
function Detalhe({
  proposta,
  questionario,
  aoFechar,
  aoRecarregar,
  aoCalcular,
}: {
  proposta: Proposta;
  questionario: Pergunta[];
  aoFechar: () => void;
  aoRecarregar: () => Promise<void>;
  aoCalcular: () => void;
}) {
  // `<dialog>` nativo, como o ModalVigencias: o navegador dá foco preso, Esc para
  // fechar e a camada de topo de graça — e nenhuma das três sai de graça à mão.
  const [editando, setEditando] = useState(false);

  useEffect(() => {
    function aoTeclar(e: KeyboardEvent) {
      if (e.key === "Escape") aoFechar();
    }
    document.addEventListener("keydown", aoTeclar);
    return () => document.removeEventListener("keydown", aoTeclar);
  }, [aoFechar]);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-navy-ink/50 p-4 backdrop-blur-sm">
      <div className="mx-auto my-8 max-w-3xl rounded-2xl bg-superficie p-6 shadow-2xl sm:p-8">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="font-display text-xl font-bold text-titulo">{nomeDoCliente(proposta)}</h2>
            <p className="mt-1 text-sm text-cinza">
              Recebida em {formatarData(proposta.criado_em)} · declaração de{" "}
              {proposta.ano_calendario} (exercício {proposta.ano_calendario + 1})
            </p>
          </div>
          <button type="button" onClick={aoFechar} className="btn-contorno shrink-0">
            Fechar
          </button>
        </div>

        <div className="mt-6 space-y-6">
          <DadosDoCliente
            proposta={proposta}
            editando={editando}
            aoEditar={() => setEditando(true)}
            aoSalvar={async () => {
              setEditando(false);
              await aoRecarregar();
            }}
            aoCancelar={() => setEditando(false)}
            aoCalcular={aoCalcular}
          />
          <RespostasDoQuestionario proposta={proposta} questionario={questionario} />
          <ContratoDaProposta proposta={proposta} />
        </div>
      </div>
    </div>
  );
}

function DadosDoCliente({
  proposta,
  editando,
  aoEditar,
  aoSalvar,
  aoCancelar,
  aoCalcular,
}: {
  proposta: Proposta;
  editando: boolean;
  aoEditar: () => void;
  aoSalvar: () => Promise<void>;
  aoCancelar: () => void;
  aoCalcular: () => void;
}) {
  const [campos, setCampos] = useState({
    nome: proposta.nome,
    email: proposta.email ?? "",
    telefone: mascararTelefone(proposta.telefone ?? ""),
    cpf: mascararCpf(proposta.cpf ?? ""),
    cep: mascararCep(proposta.cep ?? ""),
    endereco: proposta.endereco ?? "",
    complemento: proposta.complemento ?? "",
    bairro: proposta.bairro ?? "",
    cidade: proposta.cidade ?? "",
    estado: proposta.estado ?? "",
  });
  /**
   * Os honorários vivem em DOIS estados, e não em um.
   *
   * `honorariosTexto` é o que o campo mostra ("R$ 480,00") e `honorarios` é o que a
   * RPC recebe (480). Guardar só o texto obrigaria a desmascarar no submit, e é
   * exatamente ali que "R$ 1.234,56" viraria `Number("R$ 1.234,56")` = NaN — num
   * valor que vai para dentro de um contrato, ao lado do extenso.
   */
  const [honorariosTexto, setHonorariosTexto] = useState(moedaDeNumero(proposta.honorarios));
  const [honorarios, setHonorarios] = useState<number | null>(proposta.honorarios);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const [buscandoCep, setBuscandoCep] = useState(false);

  /** Mesma regra do formulário público: nunca sobrescreve o que já está lá. */
  async function completarPeloCep(cepDigitado: string) {
    if (!cepCompleto(cepDigitado)) return;
    setBuscandoCep(true);
    const achado = await buscarCep(cepDigitado);
    setBuscandoCep(false);
    if (!achado) return;
    setCampos((atual) => ({
      ...atual,
      endereco: atual.endereco.trim() || achado.endereco,
      bairro: atual.bairro.trim() || achado.bairro,
      cidade: atual.cidade.trim() || achado.cidade,
      estado: atual.estado.trim() || achado.estado,
    }));
  }

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setErro(null);

    // Avisa aqui em vez de deixar o banco recusar: o CPF vai para o contrato, e a
    // mensagem do Postgres não diz qual dígito está errado. A validação do banco
    // continua sendo a autoridade (constraint `cpf_do_cliente_valido`).
    if (campos.cpf.trim() !== "" && !cpfValido(campos.cpf)) {
      return setErro("O CPF não é válido. Confira os números ou deixe o campo em branco.");
    }
    if (campos.email.trim() !== "" && !emailValido(campos.email)) {
      return setErro("O e-mail não parece válido. Confira o endereço.");
    }

    setSalvando(true);
    // `honorarios ?? APAGAR_HONORARIOS`: este formulário SEMPRE mostra o campo,
    // então campo vazio quer dizer "apagar", e não "não mexer". Mandar `null`
    // aqui prenderia o valor — o aluno apagaria na tela, salvaria, e o número
    // antigo voltaria na recarga.
    const r = await atualizarCliente(proposta.id, {
      ...campos,
      honorarios: honorarios ?? APAGAR_HONORARIOS,
    });
    setSalvando(false);
    if (!r.ok) return setErro(r.mensagem);
    await aoSalvar();
  }

  if (!editando) {
    return (
      <section>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-display font-bold text-titulo">Dados do cliente</h3>
          <button
            type="button"
            onClick={aoEditar}
            className="text-sm font-semibold text-link underline decoration-link/30 underline-offset-4 hover:decoration-link"
          >
            Corrigir ou completar
          </button>
        </div>
        <dl className="mt-3 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
          <Par rotulo="E-mail" valor={proposta.email} />
          <Par rotulo="Telefone" valor={formatarTelefone(proposta.telefone)} />
          <Par rotulo="CPF" valor={formatarCpf(proposta.cpf)} />
          <Par rotulo="Honorários" valor={formatarReais(proposta.honorarios)} />
          <Par
            rotulo="Endereço"
            valor={
              proposta.endereco
                ? [proposta.endereco, proposta.complemento, proposta.bairro]
                    .filter(Boolean)
                    .join(", ")
                : null
            }
          />
          <Par
            rotulo="Cidade"
            valor={
              proposta.cidade ? `${proposta.cidade}${proposta.estado ? ` – ${proposta.estado}` : ""}` : null
            }
          />
        </dl>

        <ComoFoiOHonorario proposta={proposta} aoCalcular={aoCalcular} />
      </section>
    );
  }

  return (
    <form onSubmit={enviar}>
      <h3 className="font-display font-bold text-titulo">Dados do cliente</h3>
      <p className="mt-1 text-sm text-cinza">
        O que o cliente respondeu no questionário não muda aqui — só o cadastro dele.
      </p>
      {/* Cada campo declara a sua máscara. A lista existe para os onze não
          divergirem, e a máscara vem de `lib/mascaras.ts` — a mesma que o formulário
          público usa, para o aluno e o cliente dele nunca verem formatos
          diferentes do mesmo CPF. */}
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {(
          [
            ["nome", "Nome", undefined, "text"],
            ["cpf", "CPF", mascararCpf, "numeric"],
            ["email", "E-mail", undefined, "email"],
            ["telefone", "Telefone", mascararTelefone, "tel"],
            ["cep", "CEP", mascararCep, "numeric"],
            ["endereco", "Endereço", undefined, "text"],
            ["complemento", "Complemento", undefined, "text"],
            ["bairro", "Bairro", undefined, "text"],
            ["cidade", "Cidade", undefined, "text"],
            ["estado", "Estado (UF)", mascararUf, "text"],
          ] as Array<
            [keyof typeof campos, string, ((v: string) => string) | undefined, string]
          >
        ).map(([chave, rotulo, mascara, modo]) => (
          <label key={chave} className="block">
            <span className="mb-1 block text-sm font-semibold text-titulo">{rotulo}</span>
            <input
              type={modo === "email" ? "email" : "text"}
              value={campos[chave]}
              onChange={(e) => {
                const valor = mascara ? mascara(e.target.value) : e.target.value;
                setCampos({ ...campos, [chave]: valor });
                if (chave === "cep" && cepCompleto(valor)) void completarPeloCep(valor);
              }}
              onBlur={() => {
                if (chave === "cep") void completarPeloCep(campos.cep);
              }}
              inputMode={modo === "numeric" ? "numeric" : modo === "tel" ? "tel" : undefined}
              placeholder={
                chave === "cpf" ? "000.000.000-00"
                : chave === "cep" ? "00000-000"
                : chave === "telefone" ? "(11) 90000-0000"
                : chave === "estado" ? "SP"
                : undefined
              }
              className={CAMPO}
            />
            {chave === "cep" && buscandoCep && (
              <span className="mt-1 block text-sm text-cinza">Buscando o endereço…</span>
            )}
          </label>
        ))}

        {/* Honorários fora da lista: é o único campo com dois estados (o texto que
            aparece e o número que a RPC recebe). */}
        <label className="block">
          <span className="mb-1 block text-sm font-semibold text-titulo">Honorários</span>
          <input
            value={honorariosTexto}
            onChange={(e) => {
              const moeda = mascararMoeda(e.target.value);
              setHonorariosTexto(moeda.texto);
              setHonorarios(moeda.numero);
            }}
            inputMode="numeric"
            placeholder="R$ 0,00"
            className={CAMPO}
          />
          <span className="mt-1 block text-sm text-cinza">
            Vai para o contrato, por extenso. Em branco, sai como linha para preencher à mão.
          </span>
        </label>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <button type="submit" disabled={salvando} className="pill-cta disabled:opacity-60">
          {salvando ? "Salvando…" : "Salvar"}
        </button>
        <button type="button" onClick={aoCancelar} className="btn-contorno">
          Cancelar
        </button>
      </div>
      {erro && <Recado tipo="erro" texto={erro} />}
    </form>
  );
}

/**
 * De onde veio o número — e o atalho para a calculadora.
 *
 * Três estados, e a diferença entre eles é o que o aluno precisa saber antes de
 * mandar o contrato: honorário CALCULADO tem como ser explicado ao cliente
 * ("intermediária, dois dependentes, +15%"); honorário DIGITADO é uma decisão
 * que ele tomou e não deixou registro; e sem honorário o contrato sai com linha
 * em branco.
 *
 * O detalhe é `unknown` de propósito — vem de uma coluna `jsonb`, e tratá-lo
 * como tipado seria acreditar num formato que o banco não garante. Cada leitura
 * confere o tipo.
 */
function ComoFoiOHonorario({
  proposta,
  aoCalcular,
}: {
  proposta: Proposta;
  aoCalcular: () => void;
}) {
  const d =
    typeof proposta.honorarios_detalhe === "object" && proposta.honorarios_detalhe !== null
      ? (proposta.honorarios_detalhe as Record<string, unknown>)
      : null;

  const classificacao = typeof d?.classificacao === "string" ? d.classificacao : null;
  const motivo = typeof d?.motivo === "string" ? d.motivo : null;
  const posicionamento =
    typeof d?.posicionamento_rotulo === "string" ? d.posicionamento_rotulo : null;

  return (
    <div className="mt-3 rounded-xl bg-superficie-2 p-3.5">
      {classificacao ? (
        <p className="text-sm leading-relaxed text-cinza">
          Calculado: declaração <strong className="text-tinta">{classificacao.toLowerCase()}</strong>
          {motivo ? ` — ${motivo}` : ""}
          {posicionamento ? `, ${posicionamento.toLowerCase()}` : ""}.
        </p>
      ) : proposta.honorarios !== null ? (
        <p className="text-sm leading-relaxed text-cinza">
          Valor combinado à mão, sem passar pela calculadora.
        </p>
      ) : (
        <p className="text-sm leading-relaxed text-cinza">
          Sem honorários fechados. O contrato sai com linha em branco no lugar.
        </p>
      )}
      <button
        type="button"
        onClick={aoCalcular}
        className="mt-2 text-sm font-semibold text-link underline decoration-link/30 underline-offset-4 hover:decoration-link"
      >
        {classificacao ? "Recalcular na calculadora" : "Calcular na calculadora"}
      </button>
    </div>
  );
}

function Par({ rotulo, valor }: { rotulo: string; valor: string | null | undefined }) {
  return (
    <div>
      <dt className="text-cinza">{rotulo}</dt>
      <dd className="font-semibold text-tinta">{valor?.trim() ? valor : "—"}</dd>
    </div>
  );
}

/**
 * As respostas, com o ENUNCIADO que veio do banco.
 *
 * O enunciado não está escrito aqui de propósito: ele vem de `irp_questionario()`,
 * com o ano da temporada já interpolado. Foi assim que se pagou o preço de tê-los
 * dentro da interface no Bubble — recuperá-los exigiu abrir a página pública num
 * Chrome de verdade e marcar todo "Sim" para revelar os três condicionais.
 */
function RespostasDoQuestionario({
  proposta,
  questionario,
}: {
  proposta: Proposta;
  questionario: Pergunta[];
}) {
  const respostas = proposta.respostas ?? {};

  if (proposta.origem === "manual" && Object.keys(respostas).length === 0) {
    return (
      <section>
        <h3 className="font-display font-bold text-titulo">Respostas do questionário</h3>
        <p className="mt-2 rounded-xl bg-superficie-2 p-4 text-sm leading-relaxed text-cinza">
          Esta proposta foi cadastrada por você, então não tem questionário. Mande o link do
          formulário ao cliente se quiser as respostas dele.
        </p>
      </section>
    );
  }

  return (
    <section>
      <h3 className="font-display font-bold text-titulo">Respostas do questionário</h3>
      <ol className="mt-3 space-y-3">
        {questionario
          // Filha só aparece quando a mãe foi "Sim": era assim no formulário que o
          // cliente viu, e mostrar "Preencheu carnê-leão? —" para quem não é
          // autônomo é ruído que faz a lista parecer incompleta.
          .filter((q) => !q.pai || respostas[q.pai]?.sim === true)
          .map((q) => {
            const r = respostas[q.codigo];
            return (
              <li
                key={q.codigo}
                className={`rounded-xl p-3 ${q.pai ? "ml-4 bg-superficie-2" : "bg-superficie-2"}`}
              >
                <p className="text-sm font-semibold text-titulo">{q.rotulo}</p>
                <p className="mt-1 text-sm text-tinta">{textoDaResposta(q, r)}</p>
              </li>
            );
          })}
      </ol>
    </section>
  );
}

/** Uma resposta em português, incluindo o "não respondeu". */
function textoDaResposta(q: Pergunta, r: Proposta["respostas"][string] | undefined): string {
  if (!r) return "Não respondeu";

  // Quem diz se o número é dinheiro ou contagem é o CATÁLOGO (`valor_formato`,
  // 000069), não o slug. Antes era `q.codigo === "p1"` aqui — e uma pergunta nova
  // com campo em reais sairia como "3 dependentes" até alguém lembrar deste if.
  const valor =
    r.valor_numero !== undefined && r.valor_numero !== null
      ? q.valor_formato === "inteiro"
        ? `${r.valor_numero} ${r.valor_numero === 1 ? "dependente" : "dependentes"}`
        : formatarReais(r.valor_numero)
      : r.valor_texto?.trim() || null;

  if (q.tipo === "texto_longo") return r.valor_texto?.trim() || "Não respondeu";
  if (q.tipo === "escolha" || q.tipo === "sim_nao_na") return r.escolha ?? "Não respondeu";

  const base = r.sim === true ? "Sim" : r.sim === false ? "Não" : "Não respondeu";
  return valor ? `${base} — ${valor}` : base;
}

function ContratoDaProposta({ proposta }: { proposta: Proposta }) {
  const [gerando, setGerando] = useState(false);
  const [recado, setRecado] = useState<{ tipo: "ok" | "erro"; texto: string } | null>(null);

  async function gerar() {
    setGerando(true);
    setRecado(null);
    // `import()` no clique, e não no topo do arquivo: `contrato.ts` puxa o fflate e
    // a lógica de zip, e nada disso serve para quem só veio olhar as respostas. O
    // público do hub usa celular, e o bundle principal é o que ele paga para abrir
    // qualquer tela.
    const { montarContrato, baixar } = await import("../lib/contrato");
    const r = await montarContrato(proposta.id);
    setGerando(false);
    if (!r.ok) return setRecado({ tipo: "erro", texto: r.mensagem });

    baixar(r.arquivo, r.nome);
    setRecado({
      tipo: "ok",
      texto: r.faltando.length
        ? `Contrato baixado. Ficou em branco para você preencher: ${r.faltando.join(", ")}.`
        : "Contrato baixado, com todos os campos preenchidos.",
    });
  }

  return (
    <section>
      <h3 className="font-display font-bold text-titulo">Contrato</h3>
      <p className="mt-1 text-sm leading-relaxed text-cinza">
        O modelo de contrato de entrega da declaração, já com os dados deste cliente e os seus. Sai
        em Word para você ajustar as cláusulas que ficam em aberto.
      </p>

      {!proposta.dados_completos && (
        <p className="mt-3 rounded-xl bg-alerta-fundo p-3.5 text-sm leading-relaxed text-alerta-tinta">
          Faltam dados deste cliente (CPF, endereço, cidade ou estado). O contrato sai com linhas
          em branco no lugar. Use <strong>Corrigir ou completar</strong>, acima.
        </p>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button type="button" onClick={gerar} disabled={gerando} className="pill-cta disabled:opacity-60">
          {gerando ? "Montando…" : "Baixar contrato em Word"}
          {!gerando && <SetaDireita />}
        </button>
        {proposta.contrato_gerado_em && (
          <span className="text-sm text-cinza">
            Gerado pela última vez em {formatarData(proposta.contrato_gerado_em)}
          </span>
        )}
      </div>

      <p className="mt-3 text-sm text-cinza">
        Seus dados de escritório (CNPJ, CRC, endereço) entram no bloco do contratado. Cadastre-os
        em{" "}
        <Link to="/perfil" className="font-semibold text-link underline underline-offset-4">
          Meu Perfil
        </Link>
        .
      </p>

      {recado && <Recado tipo={recado.tipo} texto={recado.texto} />}
    </section>
  );
}

function Vazio({ children }: { children: React.ReactNode }) {
  return (
    <p className="mt-5 rounded-xl bg-superficie-2 p-6 text-center text-sm leading-relaxed text-cinza">
      {children}
    </p>
  );
}
